import {
  useEffect,
  useState,
} from 'react'
import {
  Link,
  Navigate,
} from 'react-router-dom'
import {
  CheckCircle, Loader2,
} from 'lucide-react'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'
import {
  useAuth
} from '../hooks/useAuth'
import {
  billingService,
} from '../services/api'
import {
  clearStoredReferralCode,
} from '../utils/referral'

// The webhook that persists the new subscription runs after Stripe redirects
// the user here, so poll briefly before deciding where to send them.
const POLL_INTERVAL_MS = 1000
const POLL_TIMEOUT_MS = 15000

type PollStatus = 'polling' | 'active' | 'timed-out'

export function BillingSuccessPage(): JSX.Element {
  const { user } = useAuth()
  const [pollStatus, setPollStatus] = useState<PollStatus>('polling')

  useEffect(() => {
    trackEvent(OnboardingEvents.SUBSCRIPTION_CREATED)
    // Reaching this page means checkout succeeded — the referral code has done
    // its job, so drop it to avoid re-applying it on a future checkout.
    clearStoredReferralCode()
  }, [])

  // Polling is only useful for onboarding users — completed users see the
  // success screen unconditionally, so don't burn 15 requests on them.
  // `user` may start null and resolve async, so wait for it before deciding.
  const needsOnboarding = user != null && user.setup_completed_at == null

  useEffect(() => {
    if (user == null || !needsOnboarding) return

    let cancelled = false
    const deadline = Date.now() + POLL_TIMEOUT_MS

    const poll = async (): Promise<void> => {
      while (!cancelled && Date.now() < deadline) {
        try {
          const sub = await billingService.getSubscription()
          if (sub.is_active) {
            if (!cancelled) setPollStatus('active')
            return
          }
        } catch (err) {
          // Keep polling until the deadline, but make failures visible so
          // future incidents (401 storms, 5xx, network) aren't invisible.
          console.warn('billing/subscription poll failed', err)
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
      }
      if (!cancelled) setPollStatus('timed-out')
    }

    void poll()
    return () => {
      cancelled = true
    }
  }, [user, needsOnboarding])

  // Onboarding users only get redirected once the subscription is visible to the
  // API. Otherwise the onboarding state machine races the webhook and lands
  // them back on the billing step — which is how double charges happen.
  if (needsOnboarding && pollStatus === 'active') {
    return <Navigate to="/onboarding" replace />
  }

  if (needsOnboarding && pollStatus === 'polling') {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <Loader2 className="w-10 h-10 text-primary-500 animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Finalizing your subscription…</p>
      </div>
    )
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center">
      <div className="mb-6">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
      </div>
      <h1 className="text-3xl font-bold text-gray-900 mb-4">
        Subscription activated!
      </h1>
      <p className="text-lg text-gray-600 mb-8">
        Your account is all set. You can manage your subscription at any time from Settings.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/"
          className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg font-medium hover:from-primary-700 hover:to-primary-600 transition-[background-color,box-shadow,transform] duration-200 shadow-soft hover:shadow-soft-lg active:scale-[0.96]"
        >
          Go to Home
        </Link>
        <Link
          to="/integrations"
          className="inline-flex items-center justify-center px-6 py-3 bg-white text-primary-600 rounded-lg font-medium hover:bg-primary-50 transition-[background-color,box-shadow,transform] duration-200 border-2 border-primary-200 active:scale-[0.96]"
        >
          Go to Settings
        </Link>
      </div>
    </div>
  )
}
